import { useMemo, useState } from "react";
import { Calculator, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";

const eur = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const UPLIFT = 0.12;

export default function Simulator() {
  const { t } = useLang();
  const [rooms, setRooms] = useState(40);
  const [occ, setOcc] = useState(68);
  const [adr, setAdr] = useState(180);

  const { current, projected, gain } = useMemo(() => {
    const current = rooms * (occ / 100) * adr * 365;
    const projected = current * (1 + UPLIFT);
    return { current, projected, gain: projected - current };
  }, [rooms, occ, adr]);

  const values = [rooms, occ, adr];
  const setters = [setRooms, setOcc, setAdr];
  const mins = [5, 30, 60];
  const maxs = [200, 100, 800];
  const displays = [rooms, `${occ} %`, eur.format(adr)];

  return (
    <section id="simulateur" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="card-lux overflow-hidden rounded-lg">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 sm:p-12">
              <p className="eyebrow" data-testid="simulator-eyebrow">
                <Calculator size={13} className="mr-2 inline-block -translate-y-[1px]" />
                {t.simulator.eyebrow}
              </p>
              <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-[#3B322A] sm:text-4xl" data-testid="simulator-title">
                {t.simulator.title1}
                <span className="gold-gradient-text italic">{t.simulator.titleAccent}</span>
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-[#948678]">
                {t.simulator.desc}
              </p>

              <div className="mt-10 space-y-8">
                {t.simulator.sliders.map((s, i) => (
                  <div key={s.testid}>
                    <div className="mb-3 flex items-center justify-between">
                      <label className="text-[12px] uppercase tracking-[0.16em] text-[#948678]">
                        {s.label}
                      </label>
                      <span className="font-mono-eyebrow text-sm font-semibold text-[#6E4E2E]">
                        {displays[i]}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={mins[i]}
                      max={maxs[i]}
                      step={i === 2 ? 5 : 1}
                      value={values[i]}
                      data-testid={s.testid}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setters[i](v);
                        e.target.style.setProperty("--fill", `${((v - mins[i]) / (maxs[i] - mins[i])) * 100}%`);
                      }}
                      style={{ "--fill": `${((values[i] - mins[i]) / (maxs[i] - mins[i])) * 100}%` }}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="relative flex flex-col justify-center border-t border-[#A87C4F]/25 bg-[#F0ECE4]/70 p-8 sm:p-12 lg:border-l lg:border-t-0">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#948678]">
                {t.simulator.currentLabel}
              </p>
              <p className="mt-2 font-display text-2xl font-semibold text-[#5F5647]" data-testid="simulator-current-revenue">
                {eur.format(current)}
              </p>

              <div className="gold-hairline my-8" />

              <p className="text-[11px] uppercase tracking-[0.24em] text-[#948678]">
                {t.simulator.projectedLabel}
              </p>
              <p className="gold-gradient-text mt-2 font-display text-4xl font-bold sm:text-5xl" data-testid="simulator-projected-revenue">
                {eur.format(projected)}
              </p>

              <div className="mt-8 inline-flex w-fit items-center gap-3 rounded-sm border border-[#A87C4F]/50 bg-[#A87C4F]/15 px-5 py-3">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#6E4E2E]">
                  {t.simulator.gainLabel}
                </span>
                <span className="font-display text-2xl font-bold text-[#6E4E2E]" data-testid="simulator-gain-value">
                  + {eur.format(gain)}
                </span>
              </div>

              <p className="mt-6 text-[11px] leading-relaxed text-[#948678]">
                {t.simulator.disclaimer}
              </p>

              <a
                href="#contact"
                data-testid="simulator-cta-button"
                className="btn-gold mt-8 inline-flex w-fit items-center gap-2 rounded-sm px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em]"
              >
                {t.simulator.cta}
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
